import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import '../styles/Drawer.css';

const DrawerContext = createContext(null);

/**
 * Usage anywhere inside the app:
 *
 *   const { openDrawer, closeDrawer } = useDrawer();
 *   openDrawer(<MyComponent onClose={closeDrawer} />);
 *
 * The drawer shell (backdrop + sliding panel) is handled here.
 * Pass any React node as content — it owns its own inner layout.
 */
export function DrawerProvider({ children }) {
  const [content, setContent] = useState(null);
  const [closing, setClosing] = useState(false);

  const openDrawer = useCallback((node) => {
    setClosing(false);
    setContent(node);
  }, []);

  const closeDrawer = useCallback(() => {
    setClosing(true);
  }, []);

  function handleAnimationEnd() {
    if (closing) {
      setContent(null);
      setClosing(false);
    }
  }

  // Close on Escape key
  useEffect(() => {
    if (!content) return;
    function onKey(e) { if (e.key === 'Escape') closeDrawer(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [content, closeDrawer]);

  return (
    <DrawerContext.Provider value={{ openDrawer, closeDrawer }}>
      {children}
      {content && (
        <>
          <div className={`drawer-backdrop${closing ? ' closing' : ''}`} onClick={closeDrawer} />
          <div
            className={`drawer-panel${closing ? ' closing' : ''}`}
            role="dialog"
            aria-modal="true"
            onAnimationEnd={handleAnimationEnd}
          >
            {content}
          </div>
        </>
      )}
    </DrawerContext.Provider>
  );
}

export function useDrawer() {
  return useContext(DrawerContext);
}
